import React, { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Loop } from '@/hooks/use-loops';
import SharedElement from './SharedElement';
import { formatDistanceToNow } from 'date-fns';
import { Heart, MessageCircle, Repeat2, Trash2, MessageSquareQuote, Eye, MoreHorizontal, Edit2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useTransition } from './TransitionContext';
import { ReblogAnnotation } from '@/hooks/use-reblogs';
import { motion, AnimatePresence } from 'framer-motion';
import { getDarkGradient, getStringHash } from '@/utils/anonymousAvatars';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import VerifiedBadge from '@/components/common/VerifiedBadge';
import { EditPostDialog } from '@/components/posts/EditPostDialog';
import { EditedIndicator } from '@/components/posts/EditedIndicator';
import { useEditPost } from '@/hooks/use-edit-post';
import { useLoopCommunities } from '@/hooks/use-loop-communities';
import { useMatureContent } from '@/hooks/use-mature-content';
import { AnnotationPinsOverlay } from '@/components/annotations/AnnotationPinsOverlay';

interface TransitionLoopFeedProps {
  loop: Loop & { is_reblogged_by_you?: boolean };
  onEngage?: (loopId: string) => void;
  isEngaged?: boolean;
  className?: string;
  annotations?: ReblogAnnotation;
  onPostClick?: (loop: Loop) => void;
  onLike?: (loop: Loop) => void;
  onComment?: (loop: Loop) => void;
  onReblog?: (loop: Loop) => void;
  onQuote?: (loop: Loop) => void;
  onMoreActions?: (loop: Loop) => void;
  showModerationDebug?: boolean;
}

const TransitionLoopFeed: React.FC<TransitionLoopFeedProps> = ({
  loop,
  onEngage,
  isEngaged = false,
  className,
  annotations,
  onPostClick,
  onLike,
  onComment,
  onReblog,
  onQuote,
  onMoreActions,
  showModerationDebug = false
}) => {
  const shortRelative = (date: Date) => {
    const s = formatDistanceToNow(date, { addSuffix: true });
    return s
      .replace('about ', '')
      .replace('almost ', '')
      .replace('over ', '')
      .replace('less than ', '')
      .replace(' hours', ' hrs')
      .replace(' hour', ' hr')
      .replace(' minutes', ' mins')
      .replace(' minute', ' min')
      .replace(' seconds', ' secs')
      .replace(' second', ' sec')
      .replace(' days', 'd')
      .replace(' day', 'd')
      .replace(' months', 'mo')
      .replace(' month', 'mo')
      .replace(' years', 'y')
      .replace(' year', 'y');
  };
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [showMature, setShowMature] = useState(false); // Per-post mature content reveal
  const editPost = useEditPost();
  const { data: loopCommunities } = useLoopCommunities(loop.id);
  
  // Use mature content hook for consistency with bulletins
  const { showMatureContent } = useMatureContent();
  
  // Add debugging
  console.log('TransitionLoopFeed rendering:', { loop: loop?.id, hasPostClick: !!onPostClick });
  
  // Use transition context safely
  const { setActiveItem } = useTransition();
  
  // Precompute anonymous background gradient
  const anonBg = useMemo(() => getDarkGradient(getStringHash(loop.id || 'anonymous')), [loop.id]);
  
  // Check if mature content should be blurred (consistent with bulletins)
  const shouldBlurMature = loop.is_mature && !showMatureContent && !showMature;
  
  // Check if current user can delete this post (admin or author)
  const canDelete = useMemo(() => {
    if (!user) return false;
    const isAuthor = user.id === loop.user_id;
    // We'll rely on backend validation for admin status
    return isAuthor || false; // Default to false for admin check in UI, backend will validate
  }, [user, loop.user_id]);
  
  // Navigate to user profile
  const handleUserClick = (e: React.MouseEvent, username?: string) => {
    e.stopPropagation();
    if (username) {
      navigate(`/user/${username}`);
    }
  };
  
  // Handle delete confirmation
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };
  
  // Handle actual deletion
  const handleDeleteConfirm = () => {
    if (onMoreActions) {
      onMoreActions(loop); // Repurposing onMoreActions to handle deletion
    }
    setDeleteDialogOpen(false);
  };

  // Handle user clicks on the post to open the detailed view
  const handlePostClick = () => {
    if (onPostClick) {
      onPostClick(loop);
    }

    // Set this post as the active transition item
    setActiveItem({ id: loop.id, data: loop });
    
    // Call onEngage if provided
    if (onEngage) {
      onEngage(loop.id);
    }
  };

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onLike) onLike(loop);
  };

  const handleComment = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onComment) onComment(loop);
  };

  const handleReblog = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onReblog) onReblog(loop);
  };

  const handleQuote = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onQuote) onQuote(loop);
  };

  const handleViewTrail = (e: React.MouseEvent) => {
    e.stopPropagation();
    // This should open TrailLens dialog via the same handler that's used for 'View Trail'
    if (onMoreActions) onMoreActions(loop);
  };

  return (
    <>
      <div 
        className={cn(
          "relative bg-[#1A1A1B] shadow-md border border-[#343536] sm:rounded-xl p-4 sm:mb-4 mb-2 text-[#D7DADC] cursor-pointer",
          className
        )}
        onClick={handlePostClick}
      >
        <AnnotationPinsOverlay postId={loop.id} postType="loop" />
        
        {/* Reblog banner */}
        {annotations?.rebloggedBy && (
          <div className="-mt-1 mb-2 text-xs text-[#818384] flex items-center gap-1">
            <Repeat2 size={14} className="text-[#818384]" />
            <span>
              Relooped by <span className="font-medium text-[#D7DADC]">@{annotations.rebloggedBy.username}</span>
            </span>
          </div>
        )}

        {/* Author info - show actual info if engaged, otherwise animated anonymous UI */}
        <AnimatePresence>
          {isEngaged ? (
            <motion.div
              initial={{ opacity: 0, height: 'auto' }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <SharedElement 
                id={`${loop.id}-header`}
                className="flex items-center gap-3 mb-3"
              >
                <div 
                  onClick={(e) => handleUserClick(e, loop.user?.username)}
                  className="cursor-pointer"
                >
                  <Avatar className="ring-2 ring-white/10 rounded-full overflow-hidden">
                    <AvatarImage 
                      src={loop.user?.profile_image_url || ''} 
                      className="object-cover w-full h-full" 
                    />
                    <AvatarFallback className="text-base">{(loop.user?.username || 'U')[0].toUpperCase()}</AvatarFallback>
                  </Avatar>
                </div>
                <div>
                  {/* Primary line: @username + admin badge + community/tag pill */}
                  <div 
                    className="font-medium hover:underline cursor-pointer flex items-center gap-2"
                    onClick={(e) => handleUserClick(e, loop.user?.username)}
                  >
                    <span>@{loop.user?.username}</span>
                    {loop.user?.is_admin && (
                      <VerifiedBadge type="admin" size={16} className="inline-block" />
                    )}
                    {loop.user?.is_partner && (
                      <VerifiedBadge type="partner" size={16} className="inline-block" />
                    )}
                    {/* Community/tag pill if available on user */}
                    {(loop.user as any)?.tag_code ? (
                      <span className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] leading-none">
                        {(loop.user as any)?.tag_emote ? (
                          <img
                            src={`https://cdn.jsdelivr.net/gh/twitter/twemoji@latest/assets/72x72/${Array.from(String((loop.user as any).tag_emote || '')).map(c => c.codePointAt(0)!.toString(16)).join('-')}.png`}
                            alt={(loop.user as any).tag_emote}
                            className="h-3.5 w-3.5"
                            style={{ imageRendering: 'pixelated' as any }}
                          />
                        ) : null}
                        <span className="font-mono tracking-widest">{(loop.user as any).tag_code}</span>
                      </span>
                    ) : null}
                  </div>
                  {/* Secondary line: display name (muted) • timestamp • community */}
                  <div className="flex items-center gap-2 text-xs text-[#818384]">
                    <span>{loop.user?.display_name}</span>
                    <span>• {loop.created_at && shortRelative(new Date(loop.created_at))}</span>
                    {loop.edited_at && <EditedIndicator editedAt={loop.edited_at} />}
                    {loopCommunities && loopCommunities.length > 0 && (
                      <>
                        <span className="text-[#9CA3AF]">→</span>
                        {loopCommunities.map((lc, index) => (
                          <span key={lc.community.id} className="inline-flex items-center gap-1">
                            {index > 0 && <span className="text-[#9CA3AF]">,</span>}
                            <span
                              className="leading-none text-sm font-medium"
                              style={{
                                backgroundImage: lc.community.gradient || lc.community.color 
                                  ? `linear-gradient(90deg, ${lc.community.color || '#A78BFA'}, ${lc.community.color ? `${lc.community.color}99` : '#C4B5FD'})`
                                  : 'linear-gradient(90deg, #A78BFA, #C4B5FD)',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                                color: 'transparent',
                              }}
                            >
                              #{lc.community.name}
                            </span>
                          </span>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </SharedElement>
            </motion.div>
          ) : (
            <motion.div 
              className="flex items-center gap-3 mb-3" 
              initial={{ opacity: 0.7 }}
              animate={{ opacity: 0.8 }}
            >
              <div 
                className="w-10 h-10 rounded-full border border-border/40 flex items-center justify-center overflow-hidden shadow-sm"
                style={{ background: anonBg }}
              ></div>
              <div className="flex-1">
                <motion.div 
                  className="relative overflow-hidden w-32 h-4 text-sm font-medium text-muted-foreground opacity-60 mb-1"
                  initial={{ opacity: 0.4 }}
                  animate={{
                    opacity: [0.4, 0.6, 0.4],
                    x: [-1, 1, -1],
                    transition: { repeat: Infinity, duration: 1.5 }
                  }}
                >
                  <span className="font-mono blur-[1.5px] tracking-widest">anonymous user</span>
                </motion.div>
                <div className="text-xs text-muted-foreground/50 blur-[1px]">
                  recently posted
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-[#818384]">
                <Eye size={12} />
                <span className="w-3 h-3 bg-foreground/20 rounded-full"></span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content */}
        <div className="mb-3">
          <SharedElement 
            id={`${loop.id}-content`}
            className="mb-3"
          >
            <p className="leading-relaxed whitespace-pre-wrap text-base">
              {loop.content}
            </p>
          </SharedElement>

          {/* Quote Chips Trail UI */}
          {annotations?.quoteChips && annotations.quoteChips.length > 0 && (
            <div className="mt-3 flex flex-col gap-2">
              {annotations.quoteChips.map(chip => (
                <div 
                  key={chip.id} 
                  className="p-2 rounded-lg border border-cyan-400/30 bg-cyan-500/10 text-xs"
                >
                  {chip.user ? (
                    <div className="flex items-center gap-1.5 mb-1">
                      <Avatar className="w-4 h-4">
                        <AvatarImage src={chip.user.profile_image_url || ''} />
                        <AvatarFallback>{(chip.user.display_name || chip.user.username || '?')[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-cyan-400 font-medium">{chip.user.display_name || chip.user.username}</span>
                      <span className="text-[#818384]">{shortRelative(new Date(chip.created_at))}</span>
                    </div>
                  ) : null}
                  <p className="whitespace-pre-wrap text-[#D7DADC]">{chip.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Media content */}
        {(loop.image_url || loop.video_url) && (
          <SharedElement 
            id={`${loop.id}-media`}
            className="mb-3 inline-block max-w-full rounded-md border border-[#343536] overflow-hidden bg-[#151516] relative"
          >
            {loop.image_url ? (
              <img 
                src={loop.image_url} 
                alt="Loop media"
                className={cn(
                  "max-w-full h-auto object-contain max-h-[500px] transition-all duration-200",
                  shouldBlurMature && "blur-2xl"
                )}
                style={{ display: 'block' }}
              />
            ) : loop.video_url ? (
              <video 
                src={loop.video_url}
                poster={loop.thumbnail_url}
                className={cn(
                  "max-w-full h-auto object-contain max-h-[500px] transition-all duration-200",
                  shouldBlurMature && "blur-2xl"
                )}
                controls={!shouldBlurMature}
                playsInline
              />
            ) : null}
            
            {/* Mature content overlay */}
            {shouldBlurMature && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                <div className="text-center space-y-3 p-4">
                  <p className="text-white font-medium text-lg">Sensitive Content</p>
                  <p className="text-gray-300 text-sm max-w-xs">This post may contain mature or NSFW content</p>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMature(true);
                    }}
                    variant="outline"
                    className="bg-white/10 hover:bg-white/20 border-white/20 text-white"
                  >
                    Show Content
                  </Button>
                </div>
              </div>
            )}
          </SharedElement>
        )}
        
        {/* Actions section */}
        <div className="pt-2 border-t border-[#343536]">
          {/* Action buttons - show actual buttons if engaged, otherwise skeleton buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]',
                  loop.is_liked && 'text-[#FF4500] bg-[#3A2A24] hover:bg-[#3F2F29]'
                )}
                onClick={handleLike}
              >
                <Heart 
                  size={18} 
                  className={loop.is_liked ? 'fill-current' : ''} 
                />
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                className="p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]"
                onClick={handleComment}
              >
                <MessageCircle size={18} />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]',
                  (loop as any).is_reblogged_by_you && 'text-[#43B581] bg-[#2A3C2E] hover:bg-[#2F4133]'
                )}
                onClick={handleReblog}
              >
                <Repeat2 
                  size={18} 
                  className={(loop as any).is_reblogged_by_you ? 'fill-current' : ''}
                />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]"
                onClick={handleQuote}
              >
                <MessageSquareQuote size={18} />
              </Button>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]" 
                  aria-label="More options"
                >
                  <MoreHorizontal size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#272729] border-[#343536] text-[#D7DADC]">
                {canDelete && (
                  <>
                    <DropdownMenuItem 
                      className="focus:bg-[#2D2D2F] cursor-pointer flex items-center gap-2"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setEditDialogOpen(true);
                      }}
                    >
                      <Edit2 size={16} />
                      Edit post
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="text-red-400 focus:text-red-400 focus:bg-[#2D2D2F] cursor-pointer flex items-center gap-2"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleDeleteClick(e);
                      }}
                    >
                      <Trash2 size={16} />
                      Delete post
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        {/* Attribution bar - only shown when post is liked or engaged */}
        {(loop.is_liked || isEngaged) && (
          <div className="mt-3 -mx-4 -mb-4 px-4 py-2 bg-[#272729] flex items-center justify-between text-xs rounded-b-xl">
            <div className="flex items-center gap-2">
              <span className="text-[#818384]">Original by</span>
              <span className="font-medium text-[#D7DADC] flex items-center gap-1.5">
                @{loop.user?.username}
                {loop.user?.is_admin && (
                  <VerifiedBadge type="admin" size={16} className="inline-block" />
                )}
                {loop.user?.is_partner && (
                  <VerifiedBadge type="partner" size={16} className="inline-block" />
                )}
              </span>
              
              {/* Metrics - only shown when post is liked */}
              {loop.is_liked && (
                <div className="flex items-center gap-2 ml-2">
                  <span className="flex items-center gap-1">
                    <Heart size={12} className="text-[#FF4500] fill-[#FF4500]" />
                    <span>{loop.like_count || 0}</span>
                  </span>
                  
                  <span className="flex items-center gap-1">
                    <Repeat2 size={12} className="text-[#818384]" />
                    <span>{loop.reblog_count || 0}</span>
                  </span>
                  
                  <span className="flex items-center gap-1">
                    <MessageCircle size={12} className="text-[#818384]" />
                    <span>{loop.comment_count || 0}</span>
                  </span>
                </div>
              )}
              
              {/* Relevance topic - only shown when engaged */}
              {loop.relevance_topic && (
                <span className="ml-2 text-[#818384]">
                  Why this post: <span className="font-medium text-[#D7DADC]">#{loop.relevance_topic}</span>
                </span>
              )}
            </div>
            
            {/* View trail button */}
            <button 
              className="text-blue-400 hover:underline"
              onClick={handleViewTrail}
            >
              View trail
            </button>
          </div>
        )}
        {showModerationDebug && loop.moderation_debug && (
          <div className="mt-3 text-xs text-[#e5e7eb] bg-[#312f40] border border-[#544f76] rounded-lg p-3 space-y-1">
            <div className="font-semibold text-sm text-[#f9fafb]">Moderation Debug</div>
            <div className="flex flex-wrap gap-4">
              <div>
                <span className="text-[#a5b4fc] mr-1">Adult score:</span>
                <span>{loop.moderation_debug.adultScore !== undefined ? loop.moderation_debug.adultScore.toFixed(2) : 'n/a'}</span>
              </div>
              <div>
                <span className="text-[#a5b4fc] mr-1">Racy score:</span>
                <span>{loop.moderation_debug.racyScore !== undefined ? loop.moderation_debug.racyScore.toFixed(2) : 'n/a'}</span>
              </div>
              <div>
                <span className="text-[#a5b4fc] mr-1">Thresholds:</span>
                <span>
                  adult ≥ {loop.moderation_debug.thresholds?.adult?.toFixed(2) ?? 'n/a'},
                  racy ≥ {loop.moderation_debug.thresholds?.racy?.toFixed(2) ?? 'n/a'}
                </span>
              </div>
              <div>
                <span className="text-[#a5b4fc] mr-1">Flagged:</span>
                <span>{loop.is_mature ? 'Yes' : 'No'}</span>
              </div>
            </div>
            <div className="text-[#94a3b8]">
              SafeSearch: {JSON.stringify(loop.moderation_debug, null, 0)}
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your post.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit post dialog */}
      <EditPostDialog
        isOpen={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        post={{
          id: loop.id,
          content: loop.content,
          type: 'loop',
          image_url: loop.image_url,
          thumbnail_url: loop.thumbnail_url,
          is_artstream: loop.is_artstream,
          communities: (loopCommunities || []).map((lc) => ({
            id: lc.community.id,
            name: lc.community.name,
            display_name: lc.community.display_name,
          })),
        }}
        onSave={async (updates) => {
          await editPost.mutateAsync({
            postId: loop.id,
            postType: 'loop',
            updates,
          });
        }}
      />
    </>
  );
};

export default TransitionLoopFeed;
